/**
 * File Picker Demo Component
 * Demo component to test file picker functionality
 */

import React from "react";
import { Upload, Image, Video, Music, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useFilePicker } from "../hooks/use-file-picker";
// FilePickerModalWrapper App.tsx'te global olarak eklendi
import type { SelectedFile } from "../types/file-picker-types";

export const FilePickerDemo: React.FC = () => {
  const {
    openPicker,
    openImagePicker,
    openVideoPicker,
    openAudioPicker,
    openDocumentPicker,
    openMultipleImagePicker,
  } = useFilePicker();

  // Handle file selection
  const handleFileSelect = (files: SelectedFile[]) => {
    console.log("✅ Seçilen dosyalar:", files);

    // Create a nice notification
    const fileNames = files.map((f) => f.name).join(", ");
    const message = `🎉 ${files.length} dosya seçildi!\n\n📁 Dosyalar:\n${fileNames}\n\n💡 Detaylar için konsolu kontrol edin.`;
    alert(message);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-bg-primary to-bg-secondary dark:from-bg-primary dark:to-bg-secondary">
      <div className="container mx-auto px-4 py-8 space-y-8">
        {/* Header */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-text-brand-600 dark:bg-text-brand-600 mb-4">
            <Upload className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-text-primary-900 dark:text-text-primary-900 mb-2">
            🚀 Universal File Picker Demo
          </h1>
          <p className="text-lg text-text-tertiary dark:text-text-tertiary max-w-2xl mx-auto">
            Kapsamlı dosya seçim sistemini test edin. Bilgisayarınızdan,
            kütüphanenizden veya Google Drive'dan dosya seçebilirsiniz.
          </p>
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-sm font-medium">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            Sistem Aktif ve Hazır
          </div>
        </div>

        {/* Demo Buttons */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 max-w-6xl mx-auto">
          {/* General File Picker */}
          <div className="group">
            <Button
              onClick={() =>
                openPicker({
                  title: "Dosya Seç",
                  maxSelection: 5,
                  onSelect: handleFileSelect,
                })
              }
              className="w-full h-28 md:h-32 flex flex-col items-center gap-2 md:gap-3 bg-bg-primary dark:bg-bg-primary hover:bg-bg-secondary dark:hover:bg-bg-secondary border-2 border-border-secondary dark:border-border-secondary hover:border-text-brand-400 dark:hover:border-text-brand-400 transition-all duration-200 group-hover:scale-105"
              variant="outline"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-text-brand-100 dark:bg-text-brand-900 flex items-center justify-center group-hover:bg-text-brand-600 dark:group-hover:bg-text-brand-600 transition-colors">
                <Upload className="w-5 h-5 md:w-6 md:h-6 text-text-brand-600 dark:text-text-brand-400 group-hover:text-white" />
              </div>
              <div className="text-center">
                <div className="text-sm md:text-base font-semibold text-text-primary-900 dark:text-text-primary-900">
                  Genel Dosya Seçici
                </div>
                <div className="text-xs text-text-tertiary dark:text-text-tertiary">
                  Tüm dosya tipleri • Max 5
                </div>
              </div>
            </Button>
          </div>

          {/* Image Picker */}
          <div className="group">
            <Button
              onClick={() =>
                openImagePicker({
                  maxSelection: 3,
                  onSelect: handleFileSelect,
                })
              }
              className="w-full h-32 flex flex-col items-center gap-3 bg-bg-primary dark:bg-bg-primary hover:bg-bg-secondary dark:hover:bg-bg-secondary border-2 border-border-secondary dark:border-border-secondary hover:border-blue-400 dark:hover:border-blue-400 transition-all duration-200 group-hover:scale-105"
              variant="outline"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center group-hover:bg-blue-600 dark:group-hover:bg-blue-600 transition-colors">
                <Image className="w-5 h-5 md:w-6 md:h-6 text-blue-600 dark:text-blue-400 group-hover:text-white" />
              </div>
              <div className="text-center">
                <div className="text-sm md:text-base font-semibold text-text-primary-900 dark:text-text-primary-900">
                  Görsel Seç
                </div>
                <div className="text-xs text-text-tertiary dark:text-text-tertiary">
                  JPG, PNG, GIF • Max 3
                </div>
              </div>
            </Button>
          </div>

          {/* Video Picker */}
          <div className="group">
            <Button
              onClick={() =>
                openVideoPicker({
                  maxSelection: 2,
                  onSelect: handleFileSelect,
                })
              }
              className="w-full h-32 flex flex-col items-center gap-3 bg-bg-primary dark:bg-bg-primary hover:bg-bg-secondary dark:hover:bg-bg-secondary border-2 border-border-secondary dark:border-border-secondary hover:border-purple-400 dark:hover:border-purple-400 transition-all duration-200 group-hover:scale-105"
              variant="outline"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center group-hover:bg-purple-600 dark:group-hover:bg-purple-600 transition-colors">
                <Video className="w-5 h-5 md:w-6 md:h-6 text-purple-600 dark:text-purple-400 group-hover:text-white" />
              </div>
              <div className="text-center">
                <div className="text-sm md:text-base font-semibold text-text-primary-900 dark:text-text-primary-900">
                  Video Seç
                </div>
                <div className="text-xs text-text-tertiary dark:text-text-tertiary">
                  MP4, WebM • Max 2
                </div>
              </div>
            </Button>
          </div>

          {/* Audio Picker */}
          <div className="group">
            <Button
              onClick={() =>
                openAudioPicker({
                  maxSelection: 5,
                  onSelect: handleFileSelect,
                })
              }
              className="w-full h-32 flex flex-col items-center gap-3 bg-bg-primary dark:bg-bg-primary hover:bg-bg-secondary dark:hover:bg-bg-secondary border-2 border-border-secondary dark:border-border-secondary hover:border-green-400 dark:hover:border-green-400 transition-all duration-200 group-hover:scale-105"
              variant="outline"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center group-hover:bg-green-600 dark:group-hover:bg-green-600 transition-colors">
                <Music className="w-5 h-5 md:w-6 md:h-6 text-green-600 dark:text-green-400 group-hover:text-white" />
              </div>
              <div className="text-center">
                <div className="text-sm md:text-base font-semibold text-text-primary-900 dark:text-text-primary-900">
                  Ses Dosyası Seç
                </div>
                <div className="text-xs text-text-tertiary dark:text-text-tertiary">
                  MP3, WAV • Max 5
                </div>
              </div>
            </Button>
          </div>

          {/* Document Picker */}
          <div className="group">
            <Button
              onClick={() =>
                openDocumentPicker({
                  maxSelection: 3,
                  onSelect: handleFileSelect,
                })
              }
              className="w-full h-32 flex flex-col items-center gap-3 bg-bg-primary dark:bg-bg-primary hover:bg-bg-secondary dark:hover:bg-bg-secondary border-2 border-border-secondary dark:border-border-secondary hover:border-orange-400 dark:hover:border-orange-400 transition-all duration-200 group-hover:scale-105"
              variant="outline"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-orange-100 dark:bg-orange-900 flex items-center justify-center group-hover:bg-orange-600 dark:group-hover:bg-orange-600 transition-colors">
                <FileText className="w-5 h-5 md:w-6 md:h-6 text-orange-600 dark:text-orange-400 group-hover:text-white" />
              </div>
              <div className="text-center">
                <div className="text-sm md:text-base font-semibold text-text-primary-900 dark:text-text-primary-900">
                  Belge Seç
                </div>
                <div className="text-xs text-text-tertiary dark:text-text-tertiary">
                  PDF, DOC, TXT • Max 3
                </div>
              </div>
            </Button>
          </div>

          {/* Multiple Images */}
          <div className="group">
            <Button
              onClick={() =>
                openMultipleImagePicker({
                  maxSelection: 10,
                  onSelect: handleFileSelect,
                })
              }
              className="w-full h-32 flex flex-col items-center gap-3 bg-bg-primary dark:bg-bg-primary hover:bg-bg-secondary dark:hover:bg-bg-secondary border-2 border-border-secondary dark:border-border-secondary hover:border-pink-400 dark:hover:border-pink-400 transition-all duration-200 group-hover:scale-105"
              variant="outline"
            >
              <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-pink-100 dark:bg-pink-900 flex items-center justify-center group-hover:bg-pink-600 dark:group-hover:bg-pink-600 transition-colors">
                <Image className="w-5 h-5 md:w-6 md:h-6 text-pink-600 dark:text-pink-400 group-hover:text-white" />
              </div>
              <div className="text-center">
                <div className="text-sm md:text-base font-semibold text-text-primary-900 dark:text-text-primary-900">
                  Çoklu Görsel
                </div>
                <div className="text-xs text-text-tertiary dark:text-text-tertiary">
                  Toplu seçim • Max 10
                </div>
              </div>
            </Button>
          </div>
        </div>

        {/* Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="text-center p-6 rounded-xl bg-bg-primary dark:bg-bg-primary border border-border-secondary dark:border-border-secondary">
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center mx-auto mb-4">
              <Upload className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="font-semibold text-text-primary-900 dark:text-text-primary-900 mb-2">
              Drag & Drop
            </h3>
            <p className="text-sm text-text-tertiary dark:text-text-tertiary">
              Dosyaları sürükleyip bırakarak kolayca yükleyin
            </p>
          </div>

          <div className="text-center p-6 rounded-xl bg-bg-primary dark:bg-bg-primary border border-border-secondary dark:border-border-secondary">
            <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center mx-auto mb-4">
              <Image className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="font-semibold text-text-primary-900 dark:text-text-primary-900 mb-2">
              Kütüphane
            </h3>
            <p className="text-sm text-text-tertiary dark:text-text-tertiary">
              Daha önce yüklediğiniz dosyalara erişin
            </p>
          </div>

          <div className="text-center p-6 rounded-xl bg-bg-primary dark:bg-bg-primary border border-border-secondary dark:border-border-secondary">
            <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900 flex items-center justify-center mx-auto mb-4">
              <Video className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="font-semibold text-text-primary-900 dark:text-text-primary-900 mb-2">
              Çoklu Format
            </h3>
            <p className="text-sm text-text-tertiary dark:text-text-tertiary">
              Görsel, video, ses ve belge desteği
            </p>
          </div>
        </div>

        {/* Usage Example */}
        <div className="max-w-4xl mx-auto p-6 rounded-xl bg-bg-quaternary dark:bg-bg-quaternary border border-border-tertiary dark:border-border-tertiary">
          <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-green-400"></span>
            Kod Örneği
          </h3>
          <pre className="text-sm text-gray-300 overflow-x-auto">
            {`const { openImagePicker } = useFilePicker();

const handleSelectImage = () => {
  openImagePicker({
    maxSelection: 5,
    onSelect: (files) => {
      console.log('Seçilen dosyalar:', files);
      // Dosyalar ile işlem yap
    },
    onCancel: () => {
      console.log('İptal edildi');
    }
  });
};`}
          </pre>
        </div>

        {/* Footer */}
        <div className="text-center py-8 border-t border-border-secondary dark:border-border-secondary">
          <p className="text-text-tertiary dark:text-text-tertiary">
            🚀 Universal File Picker sistemi başarıyla çalışıyor!
          </p>
          <p className="text-sm text-text-disabled-400 dark:text-text-disabled-400 mt-2">
            Seçilen dosyalar console'da görüntülenir
          </p>
        </div>

        {/* File Picker Modal - App.tsx'te global olarak eklendi */}
      </div>
    </div>
  );
};
